import React, { useState, useEffect } from "react";
import { Save, Globe, ShieldAlert, CheckCircle2 } from "lucide-react";

const API_URL = "https://db2sg.com/tagweigh/api/settings";

const SettingsPage = () => {
  /* =======================
     SETTINGS STATE
  ======================= */
  const [settings, setSettings] = useState({
    sync_interval: 15,
    language: "he",
    timezone: "Asia/Jerusalem",
    date_format: "DD/MM/YYYY",
    validation_required: true,
    validation_timeout: 60,
    alert_on_fail: true,
    log_failed_attempts: true,
  });

  const [saving, setSaving] = useState(false);

  /* =======================
     LOAD SETTINGS
  ======================= */
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(API_URL);
        const data = await res.json();
        if (data.settings) {
          setSettings((prev) => ({ ...prev, ...data.settings }));
        }
      } catch (e) {
        console.error("Failed to load settings", e);
      }
    };
    loadSettings();
  }, []);

  /* =======================
     SAVE SETTINGS
  ======================= */
  const saveSettings = async () => {
    try {
      setSaving(true);
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      await res.json();
      alert("ההגדרות נשמרו בהצלחה");
    } catch (e) {
      alert("שגיאה בשמירת ההגדרות");
    } finally {
      setSaving(false);
    }
  };

  /* =======================
     UI
  ======================= */
  return (
    <div className="pb-20 ltr">
      {/* Header */}
      <div className="flex justify-between items-center mb-10">
        <h2 className="text-4xl font-black">⚙️ הגדרות מערכת</h2>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-3 bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black hover:bg-emerald-700"
        >
          <Save size={20} />
          {saving ? "שומר..." : "שמור הגדרות"}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* GENERAL SETTINGS */}
        <section className="bg-white rounded-3xl shadow p-8">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="text-blue-500" />
            <h3 className="text-xl font-black">הגדרות כלליות</h3>
          </div>

          <SettingInput
            label="זמן סנכרון (דקות)"
            type="number"
            value={settings.sync_interval}
            onChange={(e) =>
              setSettings({ ...settings, sync_interval: +e.target.value })
            }
          />

          <SettingSelect
            label="שפה"
            value={settings.language}
            onChange={(e) =>
              setSettings({ ...settings, language: e.target.value })
            }
          >
            <option value="he">עברית</option>
            <option value="en">English</option>
          </SettingSelect>

          <SettingSelect
            label="אזור זמן"
            value={settings.timezone}
            onChange={(e) =>
              setSettings({ ...settings, timezone: e.target.value })
            }
          >
            <option value="Asia/Jerusalem">Asia/Jerusalem</option>
          </SettingSelect>

          <SettingSelect
            label="פורמט תאריך"
            value={settings.date_format}
            onChange={(e) =>
              setSettings({ ...settings, date_format: e.target.value })
            }
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </SettingSelect>
        </section>

        {/* VALIDATION SETTINGS */}
        <section className="bg-white rounded-3xl shadow p-8">
          <div className="flex items-center gap-3 mb-6">
            <ShieldAlert className="text-amber-500" />
            <h3 className="text-xl font-black">הגדרות אימות</h3>
          </div>

          <SettingSelect
            label="אימות חובה"
            value={settings.validation_required ? "yes" : "no"}
            onChange={(e) =>
              setSettings({
                ...settings,
                validation_required: e.target.value === "yes",
              })
            }
          >
            <option value="yes">כן</option>
            <option value="no">לא</option>
          </SettingSelect>

          <SettingInput
            label="זמן המתנה (שניות)"
            type="number"
            value={settings.validation_timeout}
            onChange={(e) =>
              setSettings({ ...settings, validation_timeout: +e.target.value })
            }
          />

          <CheckboxItem
            label="הצג התראה בעת כשלון אימות"
            checked={settings.alert_on_fail}
            onChange={(v) => setSettings({ ...settings, alert_on_fail: v })}
          />

          <CheckboxItem
            label="תעד ניסיונות אימות כושלים"
            checked={settings.log_failed_attempts}
            onChange={(v) =>
              setSettings({ ...settings, log_failed_attempts: v })
            }
          />
        </section>
      </div>
    </div>
  );
};

/* =======================
   HELPERS
======================= */

const SettingInput = ({ label, ...props }) => (
  <div className="mb-4">
    <label className="block text-xs font-black mb-1">{label}</label>
    <input {...props} className="w-full p-3 rounded-xl bg-slate-50 border" />
  </div>
);

const SettingSelect = ({ label, ...props }) => (
  <div className="mb-4">
    <label className="block text-xs font-black mb-1">{label}</label>
    <select {...props} className="w-full p-3 rounded-xl bg-slate-50 border">
      {props.children}
    </select>
  </div>
);

const CheckboxItem = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-3 mb-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-5 w-5"
    />
    <CheckCircle2 className={checked ? "text-emerald-500" : "text-slate-300"} />
    <span className="font-bold">{label}</span>
  </label>
);

export default SettingsPage;
